/**
 * Client-side OAuth connect flows for persistent cloud accounts.
 * These send auth codes to the server for token exchange + storage.
 */

// ---------------------------------------------------------------------------
// Google Drive — OAuth2 with offline access
// ---------------------------------------------------------------------------

export async function connectGoogleAccount(clientId: string): Promise<{ ok: boolean; error?: string; accountId?: string; email?: string }> {
  const redirectUri = window.location.origin + '/auth/google/callback';
  const scope = 'openid email profile https://www.googleapis.com/auth/drive.readonly';
  const state = crypto.randomUUID();

  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', scope);
  authUrl.searchParams.set('access_type', 'offline');
  authUrl.searchParams.set('prompt', 'consent');
  authUrl.searchParams.set('state', state);

  // Open popup and wait for auth code
  const code = await new Promise<string>((resolve, reject) => {
    const popup = window.open(authUrl.toString(), '_blank', 'width=500,height=700,popup=yes');
    if (!popup) {
      reject(new Error('Popup blocked. Please allow popups for this site.'));
      return;
    }

    const checkInterval = setInterval(() => {
      try {
        if (popup.location?.origin === window.location.origin) {
          const url = new URL(popup.location.href);
          clearInterval(checkInterval);
          popup.close();

          const authCode = url.searchParams.get('code');
          const errorCode = url.searchParams.get('error');
          if (authCode) {
            resolve(authCode);
          } else if (errorCode === 'access_denied') {
            reject(new Error('cancelled'));
          } else {
            reject(new Error(url.searchParams.get('error_description') || errorCode || 'No authorization code received'));
          }
        }
      } catch {
        // Cross-origin — popup hasn't redirected yet
      }
      if (popup.closed) {
        clearInterval(checkInterval);
        reject(new Error('cancelled'));
      }
    }, 200);
  });

  // Send to server for token exchange
  const res = await fetch('/api/cloud-accounts/google/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirectUri }),
  });

  return res.json() as Promise<{ ok: boolean; error?: string; accountId?: string; email?: string }>;
}

// ---------------------------------------------------------------------------
// Microsoft OneDrive — OAuth2 with PKCE + offline_access
// ---------------------------------------------------------------------------

async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer: Uint8Array): string {
  let str = '';
  for (const byte of buffer) str += String.fromCharCode(byte);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function connectMicrosoftAccount(
  clientId: string,
  tenantId: string,
): Promise<{ ok: boolean; error?: string; accountId?: string; email?: string }> {
  const redirectUri = window.location.origin + '/auth/microsoft/callback';
  const scope = 'openid offline_access Files.Read.All User.Read';
  const state = crypto.randomUUID();
  const codeVerifier = await generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const authUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('scope', scope);
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  authUrl.searchParams.set('prompt', 'select_account');

  // Clear any stale callback data
  localStorage.removeItem('oauth_callback');

  // Open popup — Microsoft's COOP: same-origin will sever the window reference,
  // so we use localStorage + storage event instead of polling popup.location.
  const popup = window.open(authUrl.toString(), '_blank', 'width=500,height=700,popup=yes');
  if (!popup) {
    throw new Error('Popup blocked. Please allow popups for this site.');
  }

  const code = await new Promise<string>((resolve, reject) => {
    let resolved = false;

    // Listen for the callback page writing to localStorage
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'oauth_callback' || !e.newValue) return;
      if (resolved) return;
      resolved = true;
      cleanup();
      try {
        const data = JSON.parse(e.newValue) as { code?: string; state?: string; error?: string; errorDescription?: string };
        localStorage.removeItem('oauth_callback');
        if (data.code) {
          resolve(data.code);
        } else if (data.error === 'access_denied') {
          reject(new Error('cancelled'));
        } else {
          reject(new Error(data.errorDescription || data.error || 'No authorization code received'));
        }
      } catch {
        reject(new Error('Invalid OAuth callback data'));
      }
    };

    // Poll localStorage directly — storage event only fires cross-tab,
    // and popup.closed may report true due to COOP severing the reference.
    // We give the popup plenty of time before considering it "closed by user".
    let closedSeenCount = 0;
    const pollInterval = setInterval(() => {
      if (resolved) return;

      // Check localStorage first
      const stored = localStorage.getItem('oauth_callback');
      if (stored) {
        resolved = true;
        cleanup();
        try {
          const data = JSON.parse(stored) as { code?: string; state?: string; error?: string; errorDescription?: string };
          localStorage.removeItem('oauth_callback');
          if (data.code) {
            resolve(data.code);
          } else if (data.error === 'access_denied') {
            reject(new Error('cancelled'));
          } else {
            reject(new Error(data.errorDescription || data.error || 'No authorization code received'));
          }
        } catch {
          reject(new Error('Invalid OAuth callback data'));
        }
        return;
      }

      // Only treat popup.closed as user-cancelled after seeing it closed
      // multiple times (COOP may report closed immediately)
      try {
        if (popup.closed) {
          closedSeenCount++;
          // Wait ~10 seconds of "closed" readings before giving up,
          // to allow time for localStorage write to arrive
          if (closedSeenCount > 20) {
            resolved = true;
            cleanup();
            reject(new Error('cancelled'));
          }
        }
      } catch { /* ignore cross-origin errors */ }
    }, 500);

    // Absolute timeout — 3 minutes
    const timeout = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      cleanup();
      reject(new Error('Sign-in timed out'));
    }, 180_000);

    const cleanup = () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };

    window.addEventListener('storage', onStorage);
  });

  // Exchange code for tokens CLIENT-SIDE (required for SPA platform in Azure)
  const tokenUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`;
  const tokenBody = new URLSearchParams({
    client_id: clientId,
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
    scope,
  });

  const tokenRes = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: tokenBody.toString(),
  });

  if (!tokenRes.ok) {
    const err = await tokenRes.json().catch(() => ({}));
    throw new Error((err as any).error_description || 'Token exchange failed');
  }

  const tokenData = (await tokenRes.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  // Send tokens to server for storage (server encrypts refresh token)
  const res = await fetch('/api/cloud-accounts/microsoft/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
    }),
  });

  return res.json() as Promise<{ ok: boolean; error?: string; accountId?: string; email?: string }>;
}
